import { Button } from "primereact/button";
import React, { useContext, useEffect, useRef, useState } from "react";

import {
  Drawer,
  PanelWithDrawers,
  TopPanel,
} from "sj-ui-toolkit/src/atlas/components/PanelWithDrawers";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts/User/UserContextProvider";
import { getExperimentClient } from "sj-ui-toolkit/src/client";
import { LessonContext, RunStatus } from "sj-ui-toolkit/src/client/svcExperimentClient";
import { getMonacoLanguage } from "sj-ui-toolkit/src/helpers";
import { Experiment, Variant } from "sj-ui-toolkit/src/api/types/experimentTypes";
import { AttemptsCount } from "sj-ui-toolkit/src/api/types/tournamentTypes";

import {
  EXPERIMENT_CODE_SUBMISSION_CHANNEL,
  TOURNAMENT_EXPERIMENT_CODE_SUBMISSION_CHANNEL,
  initCable,
} from "sj-ui-toolkit/src/client/actionCable";

import { useLearnContext } from "../../../../../context/LearnContext";
import { useTournamentContext } from "../../../../../context/TournamentContext";
import { VideoContext } from "../../VideoContext";

import ExerciseLogs from "./ExerciseLogs";

import { useMonacoThemes } from "../../../../../hooks/useMonacoThemes";
import EditorSkeleton from "../../../SandboxLesson/RightPanel/Tabs/Editor/EditorSkeleton";

const MonacoLocal = React.lazy(
  () => import("../../../SandboxLesson/RightPanel/Tabs/Editor/MonacoLocal")
);

import S from "./_styles.module.scss";

interface ExerciseProps {
  exercise: Experiment;
  variant: Variant;
}

// Message received on the experiment code-submission ActionCable channel.
interface ExerciseSubmissionMessage {
  attemptId: number;
  status: string;
  message: string;
  points: number;
  attemptsCount: AttemptsCount[];
}

export default function Exercise({ exercise, variant }: ExerciseProps) {
  const {
    isLessonComplete,
    onQuizTake,
    onLessonFinish,
    isExercisePassed,
    onExercisePassed,
    tournamentUuid,
    exerciseOnly,
  } = useContext(VideoContext);
  const { selectedTenant } = useUserContext();
  const { isTournament } = useLearnContext();
  const { handleAttempt } = useTournamentContext();
  const eventContext = {
    tenant: selectedTenant,
    tournamentUuid,
  };

  const runIdRef = useRef<string | null>(null);
  const [logs, setLogs] = useState<RunStatus | "">("");
  const [isLogsOpen, setIsLogsOpen] = useState(false);
  const [isTestsRunning, setIsTestRunning] = useState(false);

  const { editorRef, monacoTheme, handleEditorMount } = useMonacoThemes();

  const handleRun = async () => {
    const editor = editorRef.current;
    if (!editor) return;

    setIsLogsOpen(true);
    setIsTestRunning(true);
    const experimentClient = getExperimentClient();
    const { lessonKey, lessonVersion, filePath, currentVariant } = variant;

    const { runId } = await experimentClient.runTests(
      lessonKey,
      lessonVersion,
      filePath,
      editor.getValue(),
      currentVariant,
      eventContext as LessonContext
    ).promise;

    runIdRef.current = runId ?? null;
  };

  const handleReset = () => {
    if (editorRef.current) {
      editorRef.current.setValue(variant.codeContent);
    }
    setLogs("");
  };

  const onReceive = async (message: ExerciseSubmissionMessage) => {
    if (exercise.attemptId !== message.attemptId) {
      setIsTestRunning(false);
      return;
    }

    if (message.status === "failed") {
      console.error(message.message);
      setIsTestRunning(false);
      return;
    }

    const experimentClient = getExperimentClient();
    const status = await experimentClient.checkStatus(runIdRef.current ?? "").promise;
    setLogs(status ?? "");
    setIsTestRunning(false);
    if (isTournament) {
      handleAttempt({
        maxPoints: message.points,
        newAttemptsCount: message.attemptsCount,
      });
    }
    if (status?.status === "passed") {
      onExercisePassed();
    }
  };

  useEffect(() => {
    const channel = isTournament
      ? TOURNAMENT_EXPERIMENT_CODE_SUBMISSION_CHANNEL
      : EXPERIMENT_CODE_SUBMISSION_CHANNEL;
    let isDisposed = false;
    let subscription: Awaited<ReturnType<typeof initCable>> | undefined;

    void initCable(channel, (message: ExerciseSubmissionMessage) => onReceive(message), {
      camelizeKeys: true,
    })
      .then(channelSubscription => {
        if (isDisposed) {
          channelSubscription.unsubscribe();
          return;
        }
        subscription = channelSubscription;
      })
      .catch(error => {
        console.error("Unable to subscribe to the exercise channel", error);
      });

    return () => {
      isDisposed = true;
      subscription?.unsubscribe();
    };
  }, []);

  return (
    <PanelWithDrawers resizable={true} className="h-full flex flex-col">
      <TopPanel className="bg-elevation-1 rounded-l-sm rounded-tr-sm lg:rounded-tr-none flex-1 min-h-0">
        <div className="flex flex-col h-full">
          <div className="flex justify-between items-center p-3">
            <Button
              label="Run"
              icon="pi pi-check"
              data-testid="exercise-run-button"
              onClick={handleRun}
              loading={isTestsRunning}
            />
            <Button
              label="Reset coding exercise"
              icon="pi pi-refresh"
              link
              data-testid="exercise-reset-button"
              onClick={handleReset}
            />
          </div>
          <div className="flex-1 min-h-0">
            <React.Suspense fallback={<EditorSkeleton />}>
              <MonacoLocal
                height="100%"
                theme={monacoTheme}
                className={S.editor}
                language={getMonacoLanguage(exercise.language)}
                defaultValue={variant.codeContent}
                onMount={handleEditorMount}
              />
            </React.Suspense>
          </div>
          {isLessonComplete && (
            <div className="flex justify-end items-center p-3">
              <Button
                label={`Finish ${exerciseOnly ? "exercise" : "lesson"}`}
                icon="pi pi-check"
                data-testid="exercise-finish-button"
                onClick={onLessonFinish}
              />
            </div>
          )}
          {!isLessonComplete && isExercisePassed && (
            <div className="flex justify-end items-center p-3">
              <Button
                label="Take the quiz"
                data-testid="exercise-take-quiz-button"
                onClick={onQuizTake}
              />
            </div>
          )}
        </div>
      </TopPanel>
      <Drawer
        initialHeight="50%"
        title="Logs"
        isOpen={isLogsOpen}
        onOpenToggle={open => setIsLogsOpen(open)}
        className="bg-elevation-1 rounded-tl-sm pl-3 overflow-y-auto min-w-0 overflow-hidden"
      >
        <ExerciseLogs logs={logs} isTestsRunning={isTestsRunning} />
      </Drawer>
    </PanelWithDrawers>
  );
}
